// §6.3 — "Spotlight overlay + Launchpad full-screen grid".
//
// Two things behind one overlay, and the field decides which: **empty is Launchpad**, a full-screen
// grid of every app, and **typing is Spotlight**, §12's index in a single centred field. That is
// how the platform behaves, and it is why one component covers both — they are the same overlay
// wearing two faces rather than two overlays fighting over one slot.
//
// The results come from `useSearch`, the same hook the other two launchers use, so `Enter`
// "launches or focuses" whatever it found without this file deciding which (§12, D-142).
import { useEffect, useMemo, useRef, useState } from 'react';
import { ProfileMenu } from '@/host/ProfileMenu';
import { COPY } from '@/copy';
import type { SearchEntry } from '@/kernel/search';
import { useKernel, useSearch, useStoreValue, useUnhealthyApps } from '@/kernel/react';

export function Launcher() {
  const kernel = useKernel();
  const open = useStoreValue(kernel.shellui.store, (s) => s.overlay === 'launcher');
  const unhealthy = useUnhealthyApps();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // §29.1 (D-369) — Launchpad grows the same headers the other two launchers do, from the same
  // kernel-side ordering.
  const sections = useMemo(() => (open ? kernel.launch.grouped() : []), [kernel, open]);
  const searching = query.trim().length > 0;
  const { groups, flat } = useSearch(query, open);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected(0);
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    setSelected((current) => Math.min(current, Math.max(0, flat.length - 1)));
  }, [flat.length]);

  if (!open) return null;

  const activate = (entry: SearchEntry | undefined): void => {
    if (!entry) return;
    kernel.shellui.closeOverlay();
    kernel.activateSearchResult(entry);
  };

  const launch = (appId: string): void => {
    kernel.shellui.closeOverlay();
    kernel.launch.launch(appId);
  };

  return (
    <div
      className="macos-launcher-backdrop"
      data-testid="launcher"
      data-mode={searching ? 'spotlight' : 'launchpad'}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) kernel.shellui.closeOverlay();
      }}
    >
      <div className="macos-spotlight" role="dialog" aria-label={COPY.chrome.openLauncher}>
        <input
          ref={inputRef}
          type="text"
          className="macos-spotlight-input"
          data-testid="launcher-input"
          placeholder={COPY.chrome.startSearch}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setSelected((i) => Math.min(i + 1, flat.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setSelected((i) => Math.max(i - 1, 0));
            } else if (event.key === 'Enter') {
              event.preventDefault();
              activate(flat[selected]);
            } else if (event.key === 'Escape') {
              event.preventDefault();
              kernel.shellui.closeOverlay();
            }
          }}
        />

        {searching ? (
          <div className="macos-spotlight-results" data-testid="launcher-results">
            {groups.map((group) => (
              <section
                key={group.source}
                className="macos-spotlight-group"
                data-testid={`launcher-group-${group.source}`}
              >
                <h3 className="macos-spotlight-group-name">{COPY.search[group.source]}</h3>
                <ul className="macos-spotlight-list">
                  {group.results.map((result) => {
                    const entry = result.entry;
                    const index = flat.indexOf(entry);
                    return (
                      <li key={`${entry.source}:${entry.id}`}>
                        <button
                          type="button"
                          className="macos-spotlight-result"
                          data-testid={`launcher-result-${entry.id}`}
                          data-source={entry.source}
                          data-selected={String(index === selected)}
                          data-health={
                            entry.source === 'app' && unhealthy.has(entry.id) ? 'unhealthy' : 'ok'
                          }
                          onPointerEnter={() => setSelected(index)}
                          onClick={() => activate(entry)}
                        >
                          <span className="macos-spotlight-label">{entry.label}</span>
                          {entry.detail !== undefined && (
                            <span className="macos-spotlight-detail">{entry.detail}</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
            {flat.length === 0 ? (
              <p className="macos-spotlight-empty" data-testid="launcher-no-results">
                {COPY.chrome.startNoResults}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* D-346 — the account, under Spotlight rather than in the menu bar: a skin may move it,
            and it may not be the only skin that has one. */}
        <ProfileMenu prefix="macos" />
      </div>

      {/* §6.3 — Launchpad: every app, full screen, until something is typed. */}
      {searching ? null : (
        <div className="macos-launchpad" data-testid="launchpad">
          {sections.map((section) => (
            <section
              key={`${section.category ?? ''}/${section.group ?? ''}`}
              className="macos-launchpad-section"
            >
              {section.category === null ? null : (
                <h3
                  className="macos-launchpad-group-name"
                  data-testid={`launcher-section-${section.group ?? section.category}`}
                >
                  {section.group ?? section.category}
                </h3>
              )}
              <div className="macos-launchpad-grid">
                {section.apps.map((app) => (
                  <button
                    key={app.id}
                    type="button"
                    className="macos-launchpad-tile"
                    data-testid={`launchpad-${app.id}`}
                    data-health={unhealthy.has(app.id) ? 'unhealthy' : 'ok'}
                    onClick={() => launch(app.id)}
                  >
                    <span
                      className="macos-launchpad-icon"
                      aria-hidden
                      dangerouslySetInnerHTML={{ __html: kernel.appIconSvg(app.id, 64) }}
                    />
                    <span className="macos-launchpad-label">{app.name}</span>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
